import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { CheckCircle2, FileUp, Mic, Sparkles } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { DECK_MAX_BYTES, deckRejectionMessage, useDeckUpload } from '../../../hooks/use-deck-upload'

interface ImproveScoreCtaProps {
  startupId: string
  onStartSession: () => void
}

/**
 * Bottom-of-feedback CTA offering the two ways to improve a score: pitch the AI
 * investor council, or upload a fresh pitch deck. Uploading only updates the
 * profile; the hero then offers "Run feedback" because the feedback is stale.
 * The whole card is a drag-and-drop target.
 */
export function ImproveScoreCta({ startupId, onStartSession }: ImproveScoreCtaProps) {
  // Shared with the Home wizard's upload card: two entry points to "give us
  // your deck" that uploaded it differently would be two ways to get a
  // different result.
  const { upload, isUploading, isDone, error, setError } = useDeckUpload(startupId)

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: DECK_MAX_BYTES,
    noClick: true,
    noKeyboard: true,
    disabled: isUploading,
    onDropAccepted: (files) => {
      const file = files[0]
      if (file) upload(file)
    },
    onDropRejected: (rejections) =>
      setError(
        deckRejectionMessage(rejections[0]?.errors[0]?.code, rejections[0]?.errors[0]?.message)
      ),
  })

  return (
    <section className="mt-10">
      <div
        {...getRootProps()}
        className={cn(
          'rounded-2xl border border-border/40 bg-card/30 px-7 py-8 transition-colors',
          isDragActive && 'border-primary/50 bg-primary/5'
        )}
      >
        <input {...getInputProps()} />

        <div className="mb-2 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
          <Sparkles className="size-3" />
          Keep improving your score
        </div>
        <h2 className="text-[26px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
          Ready to improve your score? <span className="text-primary">Make your next move.</span>
        </h2>
        <p className="mt-3 max-w-xl text-[14.5px] leading-relaxed text-foreground/80">
          Pitch live to our AI investor council for a deeper score, or upload a fresh pitch deck and
          run feedback on it from the top of this page.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <CtaButton size="lg" icon={<Mic />} arrow onClick={onStartSession}>
            Start AI session
          </CtaButton>

          {isDone ? (
            <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-400">
              <CheckCircle2 className="size-4" />
              Deck updated. Run feedback at the top to re-evaluate.
            </span>
          ) : (
            <SecondaryButton
              type="button"
              size="lg"
              icon={<FileUp />}
              loading={isUploading}
              onClick={open}
            >
              {isUploading ? 'Uploading…' : 'Upload new pitch deck'}
            </SecondaryButton>
          )}
        </div>

        {!isDone && (
          <p className="mt-3 text-[12.5px] text-muted-foreground">Or drag a PDF here · max 50 MB</p>
        )}
        {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
      </div>
    </section>
  )
}
